// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import { QuoteModel, QuoteSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import RfqQuoteDetailsPageTypeLink from "@insite/content-library/Components/RfqQuoteDetailsPageTypeLink";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    quote: QuoteModel;
    session: Session;
    quoteSettings: QuoteSettingsModel;
    extendedStyles?: QuoteSummaryCardStyles;
    // XNG-Changes
    showQuoteNumber?: boolean;
    showQuoteRefNumber?: boolean;
    showQuoteStatus?: boolean;
    showQuoteRequestedDate?: boolean;
    showQuoteExpirationDate?: boolean;
    showQuoteShipToAddress?: boolean;
}

type Props = OwnProps;

export interface QuoteSummaryCardStyles {
    quoteSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    quoteDetailsLinkGridItem?: GridItemProps;
    typeGridItem?: GridItemProps;
    typeHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    orderDateGridItem?: GridItemProps;
    orderDateHeadingAndText?: SmallHeadingAndTextStyles;
    expirationDateGridItem?: GridItemProps;
    expirationDateHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    userNameGridItem?: GridItemProps;
    userNameHeadingAndText?: SmallHeadingAndTextStyles;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToAddressGridItem?: GridItemProps;
    shipToAddressHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: InjectableCss;
}

export const quoteSummaryCardStyles: QuoteSummaryCardStyles = {
    quoteSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0 20px;
            max-width: 500px;
        `,
    },
    container: {
        gap: 5,
    },
    quoteDetailsLinkGridItem: { width: 12 },
    typeGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    orderDateGridItem: { width: 6 },
    expirationDateGridItem: { width: 6 },
    salesRepGridItem: { width: 6 },
    userNameGridItem: { width: 6 },
    customerGridItem: { width: 6 },
    shipToAddressGridItem: { width: 6 },
};

const QuoteSummaryCard: FC<Props> = ({
    quote,
    session,
    quoteSettings,
    showQuoteExpirationDate,
    showQuoteNumber,
    showQuoteRefNumber,
    showQuoteRequestedDate,
    showQuoteShipToAddress,
    showQuoteStatus,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(quoteSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: quote.orderDate!,
        language: session.language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    let expirationDateDisplay = "";
    if (quote.expirationDate) {
        expirationDateDisplay = getLocalizedDateTime({
            dateTime: quote.expirationDate,
            language: session.language,
            options: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            },
        });
    }

    // XNG-Changes: Change structure
    return (
        <>
            {showQuoteRequestedDate && (
                <DataTableCell translate="no" {...styles.dataCell} {...{ header: translate("Requested") }}>
                    {orderDateDisplay}
                </DataTableCell>
            )}
            {showQuoteNumber && (
                <DataTableCell translate="no" {...styles.dataCell} {...{ header: translate("Quote #") }}>
                    <RfqQuoteDetailsPageTypeLink title={quote.orderNumber} quoteId={quote.id} />
                </DataTableCell>
            )}
            {showQuoteRefNumber && (
                <DataTableCell translate="no" {...styles.dataCell} {...{ header: translate("Online Ref #") }}>
                    {quote.erpOrderNumber ? (
                        <OrderDetailPageTypeLink title={quote.erpOrderNumber} orderNumber={quote.erpOrderNumber} />
                    ) : (
                        translate("N/A")
                    )}
                </DataTableCell>
            )}
            {showQuoteStatus && (
                <DataTableCell {...styles.dataCell} {...{ header: translate("Status") }}>
                    {quote.statusDisplay}
                </DataTableCell>
            )}
            {showQuoteExpirationDate && (
                <DataTableCell translate="no" {...styles.dataCell} {...{ header: translate("Expires") }}>
                    {expirationDateDisplay}
                </DataTableCell>
            )}
            {showQuoteShipToAddress && (
                <DataTableCell translate="no" {...styles.dataCell} {...{ header: translate("Ship To Address") }}>
                    {quote.shipToFullAddress}
                </DataTableCell>
            )}
        </>
    );
};

export default QuoteSummaryCard;
